import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { User, FeaturePermissions, RoleFeatures } from '../types';

const TOKEN_KEY = 'emailcraft_token';
export const GUEST_TOKEN_KEY = 'emailcraft_guest_token';
export const MFA_DEVICE_KEY = 'emailcraft_mfa_device';

/**
 * Interceptors are registered on the GLOBAL axios instance at module-import time
 * (not inside a React effect), so they are active before any component mounts.
 * All pages that use `import axios from 'axios'` automatically get auth headers
 * and 401 handling without any changes to those files.
 *
 * Guest tokens (stored in sessionStorage) are used automatically for the
 * /guest shareable-link view and are cleared when the tab closes.
 */
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(GUEST_TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// 401 handler — wired to the AuthContext logout once the provider mounts
let _onUnauthorized: (() => void) | null = null;
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = String(err.config?.url ?? '');
    if (
      err.response?.status === 401 &&
      _onUnauthorized &&
      !url.includes('/api/auth/guest-session') &&
      !url.includes('/api/auth/login')
    ) {
      _onUnauthorized();
    }
    return Promise.reject(err);
  }
);

/**
 * Re-export the global axios as `api` so that `services/api.ts` and any new
 * code that imports `{ api }` from here uses the same intercepted instance.
 */
export const api = axios;

const DEFAULT_FEATURES: FeaturePermissions = {
  manager: { buildCampaign: true, contacts: true, emailTemplates: true, alumniImport: true, alerts: true, emailSends: true, emailQueue: true, contactsQuickActions: true, contactsBuildSegment: true, contactsEditSegment: true, contactsDeleteSegment: true, contactsImport: true, contactsManageSubscribers: true, domainLookup: true, dashboard: true, dashboardUnsubDrilldown: true, queueReviewButtons: true, queueSelectionCheckmarks: true, queueReviewModal: true, queueEditEmail: true, queueStaggerButton: false, metricsLogs: false, sendsExportRecipients: false, contactsExportList: false, contactsExportSegment: false, contactsListScoring: false, contactsAuditSegments: true, contactsUtilities: false },
  user:    { buildCampaign: true, contacts: true, emailTemplates: true, alumniImport: true, alerts: true, emailSends: true, emailQueue: true, contactsQuickActions: true, contactsBuildSegment: true, contactsEditSegment: true, contactsDeleteSegment: true, contactsImport: true, contactsManageSubscribers: true, domainLookup: true, dashboard: true, dashboardUnsubDrilldown: false, queueReviewButtons: true, queueSelectionCheckmarks: true, queueReviewModal: true, queueEditEmail: true, queueStaggerButton: false, metricsLogs: false, sendsExportRecipients: false, contactsExportList: false, contactsExportSegment: false, contactsListScoring: false, contactsAuditSegments: false, contactsUtilities: false },
};

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  featurePermissions: FeaturePermissions;
  permissionsLoaded: boolean;
}

export type LoginResult =
  | { status: 'ok' }
  | { status: 'mfa'; mfaToken: string; emailHint: string };

interface AuthContextValue extends AuthState {
  login: (emailOrUsername: string, password: string) => Promise<LoginResult>;
  verifyMfa: (mfaToken: string, code: string, rememberDevice: boolean) => Promise<void>;
  resendMfa: (mfaToken: string) => Promise<string | undefined>;
  logout: () => void;
  setError: (error: string | null) => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setFeaturePermissions: (p: FeaturePermissions) => void;
  canAccess: (featureKey: keyof RoleFeatures) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [token, setTokenState] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featurePermissions, setFeaturePermissions] = useState<FeaturePermissions>(DEFAULT_FEATURES);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const setToken = useCallback((t: string | null) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      setTokenState(t);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      setTokenState(null);
      setUserState(null);
    }
  }, []);

  const setUser = useCallback((u: User | null) => {
    setUserState(u);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
  }, [setToken]);

  // Wire the 401 handler once the setToken callback is stable
  useEffect(() => {
    _onUnauthorized = () => setToken(null);
    return () => { _onUnauthorized = null; };
  }, [setToken]);

  const applySession = useCallback((token: string, user: User, deviceToken?: string) => {
    if (deviceToken) localStorage.setItem(MFA_DEVICE_KEY, deviceToken);
    setToken(token);
    setUserState(user);
  }, [setToken]);

  const login = useCallback(async (emailOrUsername: string, password: string): Promise<LoginResult> => {
    setError(null);
    const deviceToken = localStorage.getItem(MFA_DEVICE_KEY) || undefined;
    const res = await api.post<{
      mfaRequired?: boolean;
      mfaToken?: string;
      emailHint?: string;
      user?: User;
      token?: string;
    }>('/api/auth/login', {
      emailOrUsername,
      password,
      deviceToken,
    });
    if (res.data.mfaRequired && res.data.mfaToken) {
      return { status: 'mfa', mfaToken: res.data.mfaToken, emailHint: res.data.emailHint ?? 'your email' };
    }
    if (!res.data.token || !res.data.user) {
      throw new Error('Login failed');
    }
    applySession(res.data.token, res.data.user);
    return { status: 'ok' };
  }, [applySession]);

  const verifyMfa = useCallback(async (mfaToken: string, code: string, rememberDevice: boolean) => {
    setError(null);
    const res = await api.post<{ user: User; token: string; deviceToken?: string }>(
      '/api/auth/login/verify',
      { mfaToken, code, rememberDevice }
    );
    applySession(res.data.token, res.data.user, res.data.deviceToken);
  }, [applySession]);

  const resendMfa = useCallback(async (mfaToken: string) => {
    const res = await api.post<{ emailHint?: string }>('/api/auth/login/resend', { mfaToken });
    return res.data.emailHint;
  }, []);

  // Feature flags are authenticated. Until a token exists, keep defaults and
  // mark loaded so login UI is not blocked; canAccess() is false without a user.
  useEffect(() => {
    if (!token) {
      setFeaturePermissions(DEFAULT_FEATURES);
      setPermissionsLoaded(true);
      return;
    }
    setPermissionsLoaded(false);
    axios.get<FeaturePermissions>('/api/settings/features')
      .then((res) => { setFeaturePermissions(res.data); })
      .catch(() => { /* fall back to defaults */ })
      .finally(() => { setPermissionsLoaded(true); });
  }, [token]);

  // canAccess: admin always has access; other roles check the permission matrix.
  // Returns false until permissions have loaded from the API (prevents flash of
  // unauthorized nav items on initial render).
  const canAccess = useCallback((featureKey: keyof RoleFeatures): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!permissionsLoaded) return false;
    const role = user.role as 'manager' | 'user';
    return featurePermissions[role]?.[featureKey] ?? false;
  }, [user, featurePermissions, permissionsLoaded]);

  // Auto sign-out after 2 hours of inactivity.
  // Any mouse/keyboard/touch event resets the timer.
  // JWT expiry is 24h so active sessions are never cut short.
  const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;
  useEffect(() => {
    if (!token) return;

    let timer: ReturnType<typeof setTimeout>;

    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => logout(), IDLE_TIMEOUT_MS);
    };

    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'] as const;
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [token, logout]); // eslint-disable-line react-hooks/exhaustive-deps

  // Verify stored token on mount; use AbortController to avoid stale state updates
  useEffect(() => {
    if (!token) {
      setUserState(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    api
      .get<User>('/api/auth/me', { signal: controller.signal })
      .then((res) => {
        if (!controller.signal.aborted) setUserState(res.data);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err.response?.status === 401) setToken(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [token, setToken]);

  const value: AuthContextValue = {
    user,
    token,
    loading,
    error,
    featurePermissions,
    permissionsLoaded,
    login,
    verifyMfa,
    resendMfa,
    logout,
    setError,
    setUser,
    setToken,
    setFeaturePermissions,
    canAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
